import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FileText, Download, Search, Users, CheckCircle, AlertTriangle,
  Calendar, Clock, Target, Briefcase, Home, RefreshCw
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

export default function ComplianceReportPage() {
  const { axiosAuth } = useAuth();
  const [participantId, setParticipantId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const api = axiosAuth();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await api.get('/users?role=client&limit=100');
      setClients(res.data || []);
    } catch (err) {
    }
  };

  const generateReport = async (id) => {
    const targetId = id || participantId || selectedClient;
    if (!targetId) {
      toast.error('Please select or enter a participant');
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/reports/compliance/${targetId}`);
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    }
    setLoading(false);
  };

  const downloadPDF = async () => {
    if (!report) return;
    try {
      const response = await api.get(`/reports/compliance/${report.participant.id}?format=pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance_report_${report.participant.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Compliance report downloaded!');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const getComplianceColor = (value) => {
    if (typeof value !== 'number') return 'bg-slate-100 text-slate-600';
    if (value >= 80) return 'bg-emerald-100 text-emerald-700';
    if (value >= 60) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Compliant': return 'bg-emerald-100 text-emerald-700';
      case 'Needs Improvement': return 'bg-amber-100 text-amber-700';
      case 'Non-Compliant': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="space-y-6" data-testid="compliance-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Compliance Report Generator
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Generate probation/parole compliance reports for participants
          </p>
        </div>
        {report && (
          <Button onClick={downloadPDF} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2 rounded-full">
            <Download size={16} /> Download PDF
          </Button>
        )}
      </div>

      {/* Search Section */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <Select value={selectedClient} onValueChange={(v) => { setSelectedClient(v); generateReport(v); }}>
              <SelectTrigger className="flex-1 rounded-xl">
                <SelectValue placeholder="Select a participant..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.full_name} ({client.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input 
                placeholder="Or enter Participant ID"
                value={participantId}
                onChange={(e) => setParticipantId(e.target.value)}
                className="rounded-xl w-64"
              />
              <Button onClick={() => generateReport()} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" disabled={loading}>
                <Search size={16} /> {loading ? 'Generating...' : 'Generate'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {!report && !loading && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Generate a Compliance Report</h3>
            <p className="text-muted-foreground">
              Select a participant or enter their ID to generate a detailed compliance report
              suitable for probation officers, parole officers, and court officials.
            </p>
          </CardContent>
        </Card>
      )}

      {report && (
        <>
          {/* Participant Header */}
          <Card className="border-0 shadow-sm rounded-2xl bg-gradient-to-r from-slate-50 to-slate-100">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold">{report.participant.name}</h2>
                  <p className="text-sm text-muted-foreground">
                    ID: {report.participant.id.slice(0, 8)}... | Status: {report.participant.status}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Reporting Period: {report.period.start} to {report.period.end}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-center">
                    <p className="text-3xl font-bold">{report.compliance_summary.overall_rating}%</p>
                    <Badge className={getStatusColor(report.compliance_summary.overall_status)}>
                      {report.compliance_summary.overall_status}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Compliance Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center">
                    <Calendar className="text-sky-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold">{report.compliance_summary.checkin_compliance}%</p>
                <p className="text-xs text-muted-foreground">Check-in Compliance</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                    <Users className="text-violet-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold">{report.compliance_summary.session_compliance}%</p>
                <p className="text-xs text-muted-foreground">Session Attendance</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <CheckCircle className="text-emerald-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold">
                  {typeof report.compliance_summary.drug_test_compliance === 'number' 
                    ? `${report.compliance_summary.drug_test_compliance}%` 
                    : 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground">Drug Test Compliance</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                    <Clock className="text-amber-600" size={20} />
                  </div>
                </div>
                <p className="text-2xl font-bold">
                  {typeof report.compliance_summary.curfew_compliance === 'number' 
                    ? `${report.compliance_summary.curfew_compliance}%` 
                    : 'N/A'}
                </p>
                <p className="text-xs text-muted-foreground">Curfew Compliance</p>
              </CardContent>
            </Card>
          </div>

          {/* Details Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Attendance */}
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Calendar className="text-sky-600" size={18} /> Attendance Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between p-3 bg-muted/30 rounded-xl">
                  <span>Daily Check-ins (30 days)</span>
                  <Badge className={getComplianceColor(report.attendance.daily_checkins / 30 * 100)}>
                    {report.attendance.daily_checkins} / 30
                  </Badge>
                </div>
                <div className="flex justify-between p-3 bg-muted/30 rounded-xl">
                  <span>Group Sessions Attended</span>
                  <Badge className="bg-violet-100 text-violet-700">{report.attendance.group_sessions_attended}</Badge>
                </div>
                {report.attendance.sessions?.length > 0 && (
                  <div className="pt-2">
                    <p className="text-sm font-medium mb-2">Recent Sessions:</p>
                    {report.attendance.sessions.slice(0, 5).map((session, idx) => (
                      <div key={`item-${idx}`} className="text-sm text-muted-foreground py-1 border-b last:border-0">
                        {session.date} - {session.title}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Drug Testing */}
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <CheckCircle className="text-emerald-600" size={18} /> Drug Testing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {report.drug_testing.total_tests > 0 ? (
                  <>
                    <div className="flex justify-between p-3 bg-muted/30 rounded-xl">
                      <span>Total Tests</span>
                      <Badge variant="outline">{report.drug_testing.total_tests}</Badge>
                    </div>
                    <div className="flex justify-between p-3 bg-emerald-50 rounded-xl">
                      <span>Negative Results</span>
                      <Badge className="bg-emerald-100 text-emerald-700">{report.drug_testing.negative_results}</Badge>
                    </div>
                    <div className="flex justify-between p-3 bg-red-50 rounded-xl">
                      <span>Positive Results</span>
                      <Badge className="bg-red-100 text-red-700">{report.drug_testing.positive_results}</Badge>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No drug tests recorded this period</p>
                )}
              </CardContent>
            </Card>

            {/* Curfew */}
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Clock className="text-amber-600" size={18} /> Curfew Compliance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {report.curfew.total_nights > 0 ? (
                  <>
                    <div className="flex justify-between p-3 bg-muted/30 rounded-xl">
                      <span>Total Nights Tracked</span>
                      <Badge variant="outline">{report.curfew.total_nights}</Badge>
                    </div>
                    <div className="flex justify-between p-3 bg-emerald-50 rounded-xl">
                      <span>Compliant Nights</span>
                      <Badge className="bg-emerald-100 text-emerald-700">{report.curfew.compliant_nights}</Badge>
                    </div>
                    <div className="flex justify-between p-3 bg-red-50 rounded-xl">
                      <span>Violations</span>
                      <Badge className="bg-red-100 text-red-700">{report.curfew.violations}</Badge>
                    </div>
                  </>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No curfew tracking for this participant</p>
                )}
              </CardContent>
            </Card>

            {/* Goals */}
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Target className="text-indigo-600" size={18} /> Goals Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-muted/30 rounded-xl">
                    <p className="text-xl font-bold">{report.goals.total}</p>
                    <p className="text-xs text-muted-foreground">Total</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-xl">
                    <p className="text-xl font-bold text-emerald-600">{report.goals.completed}</p>
                    <p className="text-xs text-muted-foreground">Completed</p>
                  </div>
                  <div className="text-center p-3 bg-sky-50 rounded-xl">
                    <p className="text-xl font-bold text-sky-600">{report.goals.in_progress}</p>
                    <p className="text-xs text-muted-foreground">In Progress</p>
                  </div>
                </div>
                {report.goals.goals_list?.length > 0 && (
                  <div className="pt-2">
                    <p className="text-sm font-medium mb-2">Active Goals:</p>
                    {report.goals.goals_list.slice(0, 3).map((goal, idx) => (
                      <div key={`item-${idx}`} className="flex justify-between items-center text-sm py-1.5 border-b last:border-0">
                        <span className="truncate flex-1">{goal.title}</span>
                        <Badge variant="outline" className="ml-2">{goal.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Services & Employment */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>
                  Services Received
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center p-3 bg-muted/30 rounded-xl mb-3">
                  <span>Total Service Contacts</span>
                  <Badge className="bg-indigo-100 text-indigo-700">{report.services_received.total_contacts}</Badge>
                </div>
                {report.services_received.service_types?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {report.services_received.service_types.map((type, idx) => (
                      <Badge key={`item-${idx}`} variant="outline" className="text-xs">{type.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Briefcase className="text-emerald-600" size={18} /> Employment Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.employment ? (
                  <div className="space-y-2">
                    <div className="flex justify-between p-3 bg-muted/30 rounded-xl">
                      <span>Status</span>
                      <Badge className={report.employment.current_status === 'employed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {report.employment.current_status || 'Seeking'}
                      </Badge>
                    </div>
                    {report.employment.employer && (
                      <div className="flex justify-between p-3 bg-muted/30 rounded-xl">
                        <span>Employer</span>
                        <span className="font-medium">{report.employment.employer}</span>
                      </div>
                    )}
                    {report.employment.position && (
                      <div className="flex justify-between p-3 bg-muted/30 rounded-xl">
                        <span>Position</span>
                        <span className="font-medium">{report.employment.position}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No employment data recorded</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Report Footer */}
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardContent className="p-4 text-center text-sm text-muted-foreground">
              <p>Report generated on {formatDate(report.generated_at)} by {report.generated_by}</p>
              <p className="mt-1">This report covers the 30-day period from {report.period.start} to {report.period.end}</p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
